// 自動分割（シャドーイングのおまけ機能）：音声を短い練習単位に「必ず」区切る。
// 方針（Speater 寄り）: 無音検出に頼らず「目標長で常に区切る＋できれば静かな所で切る」。
//   - 各境界は [min,max] 窓内で一番静かなフレームに置く（話し声ならポーズ、連続音なら浅い谷/最大長）。
//   - 無音が全く無くても必ず短く割れる。デコード不可（動画等）なら長さだけ取って等間隔グリッド。
//   テキストは持たない（区切りのみ）。純DSPなのでネイティブ(AVFoundation)へ移植可（PCM取得だけ差し替え）。

const TARGET_RATE = 16000
const FRAME_SEC = 0.02 // 20ms
const MIN_SEG_SEC = 0.7 // これより短くは切らない
const MAX_SEG_SEC = 3.0 // これより長くは伸ばさない（無音が無くてもここで切る）
const GRID_SEC = 2.2 // デコード不可時の等間隔グリッド幅

// メディアの長さ(秒)を要素から取得（動画でも可）。失敗時 0。
function probeDurationEl(file) {
  return new Promise((resolve) => {
    const el = document.createElement(/^video\//.test(file.type) ? 'video' : 'audio')
    const url = URL.createObjectURL(file)
    el.preload = 'metadata'
    const done = (d) => { URL.revokeObjectURL(url); resolve(Number.isFinite(d) && d > 0 ? d : 0) }
    el.onloadedmetadata = () => done(el.duration)
    el.onerror = () => done(0)
    el.src = url
  })
}

// 等間隔グリッドで cue を作る（無音情報なしでも必ず区切る）。
function gridCues(dur, step = GRID_SEC) {
  if (!dur || dur <= 0) return []
  const cues = []
  for (let s = 0; s < dur - 0.05; s += step) {
    cues.push({ start: Math.round(s * 1000) / 1000, end: Math.round(Math.min(dur, s + step) * 1000) / 1000, text: '' })
  }
  return cues
}

// File/Blob → セグメント cue 配列 [{ start, end, text:'' }]。
export async function autoSegment(file) {
  let audio = null
  try {
    const buf = await file.arrayBuffer()
    const Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext
    audio = await new Ctx(1, 1, TARGET_RATE).decodeAudioData(buf)
  } catch {
    audio = null
  }
  // デコード不可（動画コンテナ等）→ 長さだけ取って等間隔で必ず区切る。
  if (!audio) return gridCues(await probeDurationEl(file))

  const rate = audio.sampleRate
  const dur = audio.duration || 0
  const data = audio.getChannelData(0)
  if (!data || !data.length) return gridCues(dur)

  const frame = Math.max(1, Math.floor(rate * FRAME_SEC))
  const nFrames = Math.floor(data.length / frame)
  if (nFrames < 3) return gridCues(dur)

  // フレームごとの音量（RMS）。境界は「窓内で最も静かなフレーム」に置く。
  const rms = new Float32Array(nFrames)
  for (let i = 0; i < nFrames; i++) {
    let sum = 0
    const base = i * frame
    for (let j = 0; j < frame; j++) {
      const v = data[base + j]
      sum += v * v
    }
    rms[i] = Math.sqrt(sum / frame)
  }

  const minF = Math.max(1, Math.round(MIN_SEG_SEC / FRAME_SEC))
  const maxF = Math.max(minF + 1, Math.round(MAX_SEG_SEC / FRAME_SEC))
  const bounds = [] // フレーム境界（start インデックス列）
  let start = 0
  while (start < nFrames) {
    if (start + maxF >= nFrames) { bounds.push([start, nFrames]); break }
    const from = start + minF
    const to = start + maxF
    let q = from
    let qv = Infinity
    for (let i = from; i <= to; i++) {
      if (rms[i] < qv) { qv = rms[i]; q = i } // 最も静かなフレームで切る
    }
    bounds.push([start, q])
    start = q
  }

  // 時間化＋末尾の短すぎは前へ吸収。
  const cues = bounds.map(([a, b]) => ({ start: a * FRAME_SEC, end: b * FRAME_SEC }))
  if (cues.length >= 2) {
    const last = cues[cues.length - 1]
    if (last.end - last.start < MIN_SEG_SEC) {
      cues[cues.length - 2].end = last.end
      cues.pop()
    }
  }
  return cues.map((s) => ({
    start: Math.round(s.start * 1000) / 1000,
    end: Math.round(Math.min(dur || s.end, s.end) * 1000) / 1000,
    text: '',
  }))
}
